import { isEmpty, getZipNumberOfDigitsByCountry } from '@jjj/components';
import actions from './actions';
import { BANDING_TARGET_FIELD, BANDING_TIME_BAND_FIELD } from './banding';
import { createReducer } from '../../../../helpers/reducer';
import { keyBy } from '../../../../helpers/core';
import { withTZ } from '../../../../helpers/dateTime';
import { isAUS, setDefaultCountryField, formatCurrency, isPostalCode } from '../../../../helpers/countriesHelpers';
import { COUNTRIES } from '../../../../helpers/countries';
import { skillsKeysAUS, skillsKeysUSA } from '../../../../helpers/skillMapper';
import { t } from '../../../../i18n';

const positions = {
  currentPositions: [],
  futurePositions: [],
  isCurrentPositionsLoading: false,
  isFuturePositionsLoading: false,
};

const BANDING_REQUIRED_ERRORS = {
  [BANDING_TARGET_FIELD]: 'dash.admin.districts.dynamic_banding_target_required_error',
  [BANDING_TIME_BAND_FIELD]: 'dash.admin.districts.dynamic_banding_time_band_required_error',
};

const CYCLIC_ERROR_KEY = 'dash.admin.districts.dynamic_banding_cyclic_error';

const livePositions = editForm =>
  ['currentPositions', 'futurePositions'].reduce(
    // eslint-disable-next-line no-underscore-dangle
    (rows, key) => rows.concat((editForm[key] || []).filter(item => item && !item._destroy?.value)),
    [],
  );

// A mapping belongs to the position, not to the rate period it was edited in, and both tabs are
// kept identical - so any live row carrying a mapping for that id answers the question.
function getBandingTargetOf(editForm, positionId) {
  const row = livePositions(editForm).find(
    item =>
      item.id &&
      item.id.value === positionId &&
      item.banding_enabled &&
      item.banding_enabled.value &&
      item[BANDING_TARGET_FIELD]?.value,
  );

  return row ? row[BANDING_TARGET_FIELD].value : null;
}

// Writes and withdraws only the maps-back verdict. Anything else in the error slot stays: the
// API's other mapping rejections are the only record of why a save was refused.
function onRevalidateBandingMappings(state) {
  const editForm = { ...state.editForm };
  const cyclicError = t(CYCLIC_ERROR_KEY);

  livePositions(editForm).forEach(row => {
    const field = row[BANDING_TARGET_FIELD];
    const ownId = row.id && row.id.value;

    if (!field || !ownId || !row.banding_enabled?.value || !field.value) {
      return;
    }

    const isCyclic = getBandingTargetOf(editForm, field.value) === ownId;

    if (isCyclic && !field.error) {
      field.error = cyclicError;
    } else if (!isCyclic && field.error === cyclicError) {
      field.error = '';
    }
  });

  return { ...state, editForm };
}

const initState = {
  district: undefined,
  isImportModalVisible: false,
  isCsvPositionsImporting: false,
  editForm: null,
  formInfo: {
    countries: [],
  },
  editImportForm: null,
  isEdit: false,
  loading: false,
  sectors: [],
  skills: [],
  step: 1,
};

const initialEditData = {
  applicable_zip_codes: '',
  current_rate: { valid_since: '2019-07-01' },
  sectors: [],
  positions,
  ...positions,
};

const initImportData = {
  attachment: {
    name: 'attachment',
    error: '',
    touched: false,
    value: null,
  },
  isPricesAreConfirmed: {
    name: 'isPricesAreConfirmed',
    error: '',
    touched: false,
    value: true,
  },
};

function validate(attr, value, state) {
  const payRateCurrent = state?.editForm?.current_rate?.pay?.value || null;
  const payRateFuture = state?.editForm?.future_rate?.pay?.value || null;
  const minimumWageFuture = state?.editForm?.future_rate?.min?.value || null;
  const minimumWageCurrent = state?.editForm?.current_rate?.min?.value || null;

  if (attr === 'current_rate.valid_since') {
    if (isEmpty(value)) {
      return t('dash.validation.select_a_field', { fieldName: t('dash.admin.districts.start_date_label') });
    }

    if (!isEmpty(value) && withTZ(value).isAfter(withTZ())) {
      return t('dash.validation.date_in_the_past');
    }
  }

  if (attr === 'future_rate.pay' || attr === 'current_rate.pay') {
    if (isEmpty(value)) {
      return t('dash.validation.required_error', {
        fieldName: t('dash.admin.districts.districts_columns.pay_rate_title'),
      });
    }

    if (minimumWageFuture && attr === 'future_rate.pay' && value < +minimumWageFuture) {
      return t('dash.validation.greater_or_equal_error', { value: minimumWageFuture });
    }

    if (minimumWageCurrent && attr === 'current_rate.pay' && value < +minimumWageCurrent) {
      return t('dash.validation.greater_or_equal_error', { value: minimumWageCurrent });
    }
  }

  if (attr === 'future_rate.min' || attr === 'current_rate.min') {
    if (isEmpty(value)) {
      return t('dash.validation.required_error', {
        fieldName: t('dash.admin.districts.districts_columns.minimum_wage_title'),
      });
    }
  }

  if (attr === 'future_rate.bill' || attr === 'current_rate.bill') {
    if (isEmpty(value)) {
      return t('dash.validation.required_error', {
        fieldName: t('dash.admin.districts.districts_columns.bill_rate_title'),
      });
    }

    if (minimumWageCurrent && attr === 'current_rate.bill' && value < +minimumWageCurrent) {
      if (+payRateCurrent < +minimumWageCurrent) {
        return t('dash.validation.greater_or_equal_error', { value: minimumWageCurrent });
      }
    }

    if (minimumWageFuture && attr === 'future_rate.bill' && value < +minimumWageFuture) {
      if (+payRateCurrent < +minimumWageCurrent) {
        return t('dash.validation.greater_or_equal_error', { value: minimumWageFuture });
      }
    }

    if (payRateFuture && attr === 'future_rate.bill' && value < +payRateFuture) {
      return t('dash.validation.greater_or_equal_error', { value: payRateFuture });
    }

    if (payRateCurrent && attr === 'current_rate.bill' && value < +payRateCurrent) {
      return t('dash.validation.greater_or_equal_error', { value: payRateCurrent });
    }
  }

  if (attr === 'future_rate.valid_since') {
    if (isEmpty(value)) {
      return t('dash.validation.select_a_field', { fieldName: t('dash.admin.districts.start_date_label') });
    }

    if (!isEmpty(value) && withTZ(value).isBefore(withTZ(state.district.current_rate.valid_since))) {
      return t('dash.validation.date_in_the_future');
    }
  }

  if (!state || !state.editForm) {
    return '';
  }

  const { editForm, formInfo } = state;

  if (attr === 'applicable_zip_codes') {
    const districtCountry = formInfo && formInfo.countries.find(country => editForm.country.value === country.id);

    const numberOfDigits = getZipNumberOfDigitsByCountry(districtCountry?.name);
    const countryAlpha2Code = COUNTRIES[districtCountry?.name]?.alpha_2_code;

    // Validate each zip code in the comma-separated list individually
    if (value) {
      const zipCodesArray = value
        .split(',')
        .map(zip => zip.trim())
        .filter(zip => zip);
      const allZipCodesValid = zipCodesArray.every(zip => isPostalCode(zip, countryAlpha2Code));

      if (!allZipCodesValid) {
        const codeName = t('dash.common.location.postal_codes');

        const placeholder = 'X'.repeat(numberOfDigits);

        return `${t('dash.admin.districts.zip_codes_invalid', { codeName })}
          (${`${placeholder}, ${placeholder}`})`;
      }
    }
  }

  if (
    attr.match('currentPositions') &&
    (attr.match('epic.pay') || attr.match('solid.pay') || attr.match('outstanding.pay'))
  ) {
    if (editForm.current_rate && parseFloat(value) < parseFloat(editForm.current_rate.pay.value)) {
      return t('dash.admin.districts.below_baseline_pay_error', {
        value: formatCurrency(editForm.current_rate.pay.value),
      });
    }
  }

  if (
    attr.match('currentPositions') &&
    (attr.match('epic.bill') || attr.match('solid.bill') || attr.match('outstanding.bill'))
  ) {
    if (editForm.current_rate && parseFloat(value) < parseFloat(editForm.current_rate.bill.value)) {
      return t('dash.admin.districts.below_baseline_bill_error', {
        value: formatCurrency(editForm.current_rate.bill.value),
      });
    }
  }

  if (
    attr.match('futurePositions') &&
    (attr.match('epic.pay') || attr.match('solid.pay') || attr.match('outstanding.pay'))
  ) {
    if (editForm.future_rate && parseFloat(value) < parseFloat(editForm.future_rate.pay.value)) {
      return t('dash.admin.districts.below_baseline_pay_error', {
        value: formatCurrency(editForm.future_rate.pay.value),
      });
    }
  }

  if (
    attr.match('futurePositions') &&
    (attr.match('epic.bill') || attr.match('solid.bill') || attr.match('outstanding.bill'))
  ) {
    if (editForm.future_rate && parseFloat(value) < parseFloat(editForm.future_rate.bill.value)) {
      return t('dash.admin.districts.below_baseline_bill_error', {
        value: formatCurrency(editForm.future_rate.bill.value),
      });
    }
  }

  // Must precede the generic `attr.includes('id')` check: both keys end in `id` and would
  // otherwise be reported as a missing skill.
  const bandingField = Object.keys(BANDING_REQUIRED_ERRORS).find(field => attr.endsWith(field));

  if (bandingField) {
    const parts = attr.split('.');

    if (parts.length < 3) {
      return '';
    }

    const [positionsKey, index] = parts;
    const position = editForm[positionsKey] && editForm[positionsKey][index];

    if (!position || !position.banding_enabled || !position.banding_enabled.value) {
      return '';
    }

    if (isEmpty(value)) {
      return t(BANDING_REQUIRED_ERRORS[bandingField]);
    }

    return '';
  }

  if (attr.includes('id')) {
    if (isEmpty(value)) {
      return t('dash.validation.required_error', { fieldName: t('dash.admin.districts.skill_label') });
    }
  }

  if (attr.match('currentPositions') && attr.includes('.id')) {
    if (
      editForm.currentPositions &&
      editForm.currentPositions.filter(item => item && item.id && item.id.value && item.id.value === value).length
    ) {
      return t('dash.admin.districts.position_already_exists_error');
    }
  }

  if (attr.match('futurePositions') && attr.includes('.id')) {
    if (
      editForm.futurePositions &&
      editForm.futurePositions.filter(item => item && item.id && item.id.value && item.id.value === value).length
    ) {
      return t('dash.admin.districts.position_already_exists_error');
    }
  }

  if (attr.match('sectors') && attr.match('.id') && !attr.match('currentPositions')) {
    if (
      editForm.sectors &&
      editForm.sectors.filter(item => item && item.id && item.id.value && item.id.value === value).length
    ) {
      return t('dash.admin.districts.sector_already_exists_error');
    }
  }
}

function formatProp(payload, prop, state) {
  const getProp = () => ({
    name: prop,
    value: payload && payload[prop],
    error: payload && validate(prop, payload[prop], state),
    touched: false,
  });

  if (prop === 'state' || prop === 'country') {
    return {
      name: prop,
      value: payload && payload[prop].id,
      error: payload && validate(prop, payload[prop]),
      touched: false,
    };
  }

  if (prop === 'excluded_zip_codes') {
    return getProp();
  }

  if (payload && Array.isArray(payload[prop])) {
    // eslint-disable-next-line no-use-before-define
    return formatArray(payload, prop, state);
  }

  if (payload && typeof payload[prop] === 'object' && payload[prop] != null) {
    // eslint-disable-next-line no-use-before-define
    return formatObject(payload, prop, state);
  }

  return getProp();
}

function formatArray(payload, prop, state) {
  return payload[prop].map((item, index) => formatProp(payload[prop], index, state));
}

function formatObject(payload, prop, state) {
  const propsArray = Object.keys(payload[prop]);

  return keyBy(
    propsArray,
    name => name,
    name => formatProp(payload[prop], name, state),
  );
}

function getValue(attrs, value, state) {
  if (typeof value === 'object' && value !== null) {
    return keyBy(
      Object.keys(value),
      prop => prop,
      prop => formatProp(value, prop, state),
    );
  }

  return {
    name: attrs.join('.'),
    touched: true,
    error: validate(attrs.join('.'), value, state),
    value,
  };
}

function onLoad(state) {
  return { ...state, loading: true };
}

function onLoadSuccess(state, { payload }) {
  return { ...state, ...payload, loading: false };
}

function onLoadFormData(state, { payload }) {
  return { ...state, ...payload, loading: true };
}

function onLoadFormDataSuccess(state, { payload }) {
  return { ...state, ...payload, loading: false };
}

function onLoadTimeBandsSuccess(state, { payload }) {
  return { ...state, formInfo: { ...state.formInfo, time_bands: payload } };
}

function onLoadCurrentPositions(state) {
  return { ...state, district: { ...state.district, isCurrentPositionsLoading: true } };
}

function onLoadCurrentPositionsSuccess(state, { payload }) {
  return { ...state, district: { ...state.district, currentPositions: payload, isCurrentPositionsLoading: false } };
}

function onLoadFuturePositions(state) {
  return { ...state, district: { ...state.district, isFuturePositionsLoading: true } };
}

function onLoadFuturePositionsSuccess(state, { payload }) {
  return { ...state, district: { ...state.district, futurePositions: payload, isFuturePositionsLoading: false } };
}

function onLoadSectorPositionsSuccess(state, { payload }) {
  function getDefaultData(item, key, records) {
    const position = records.find(pos => item.position_id === pos.id.value);

    const defaultData = {
      name: formatProp(item, 'name', state),
      id: formatProp(item, 'position_id', state),
    };

    (isAUS() ? skillsKeysAUS : skillsKeysUSA).map(tier => {
      if (position[tier]) {
        defaultData[tier] = {
          bill: { name: `${key}.${tier}.bill`, value: position[tier].bill.value, error: '', touched: false },
          pay: { name: `${key}.${tier}.pay`, value: position[tier].pay.value, error: '', touched: false },
          sector_id: {
            name: `${key}.${tier}.sector_id`,
            value: payload?.id,
            error: '',
            touched: false,
          },
        };

        if (position[tier].skill_id?.value) {
          defaultData[tier].skill_id = {
            name: `${key}.${tier}.skill_id`,
            value: position[tier].skill_id?.value,
            error: '',
            touched: false,
          };
        }
      }

      return tier;
    });

    return defaultData;
  }

  const sectors = [...state.editForm.sectors];
  const currentPositionIds = state.editForm.currentPositions.map(cp => cp.id.value);
  const futurePositionIds = state.editForm.futurePositions.map(cp => cp.id.value);

  const { records } = payload.data;
  const sector = sectors[payload.index];
  sector.loading.value = false;
  sector.positionsLoaded.value = true;
  sector.currentPositions = {
    position_rates: records
      .filter(position => currentPositionIds.includes(position.position_id))
      .map(position => getDefaultData(position, 'currentPositions', state.editForm.currentPositions)),
  };
  sector.futurePositions = {
    position_rates: records
      .filter(position => futurePositionIds.includes(position.position_id))
      .map(item => getDefaultData(item, 'futurePositions', state.editForm.futurePositions)),
  };
  const index = sectors.indexOf(sector);
  sectors.splice(index, 1, sector);

  return { ...state, editForm: { ...state.editForm, sectors } };
}

function onClearStore() {
  return { ...initState };
}

function onEdit(state, { payload }) {
  const { district } = payload;
  const propsArray = Object.keys(district);

  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => formatProp(payload.district, prop, state),
  );

  const editImportForm = initImportData;

  return {
    ...state,
    isEdit: true,
    step: payload.step || 1,
    editForm,
    editImportForm: {
      ...editImportForm,
    },
  };
}

function onSaveSuccess(state) {
  return {
    ...state,
    isEdit: false,
    loading: true,
    editForm: null,
  };
}

function onSaveFail(state, { payload }) {
  return {
    ...state,
    isEdit: true,
    loading: false,
    ...payload,
  };
}

function onCreate(state) {
  const propsArray = Object.keys(initialEditData);

  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => formatProp(initialEditData, prop, state),
  );

  const editImportForm = initImportData;

  return {
    ...state,
    isEdit: true,
    district: initialEditData,
    editForm: {
      ...editForm,
      country: setDefaultCountryField(),
    },
    editImportForm: {
      ...editImportForm,
    },
  };
}

function onAddSuccess(state) {
  return {
    ...state,
    isEdit: false,
    loading: true,
    editForm: null,
  };
}

function onAddFail(state, { payload }) {
  return {
    ...state,
    isEdit: true,
    loading: false,
    ...payload,
  };
}

function onToggleImportModal(state, { payload }) {
  const { isImportModalVisible } = payload;

  return {
    ...state,
    isImportModalVisible,
  };
}

function onImportCsvPositions(state) {
  return {
    ...state,
    isCsvPositionsImporting: true,
  };
}

function onImportCsvPositionsSuccess(state) {
  return {
    ...state,
    isCsvPositionsImporting: false,
  };
}

function onImportCsvPositionsError(state) {
  return {
    ...state,
    isCsvPositionsImporting: false,
  };
}

function onChangeProperty(state, { payload }) {
  const { attr, value, silent } = payload;
  const { editForm, editImportForm } = state;

  if (editImportForm && editImportForm[attr]) {
    editImportForm[attr] = {
      value,
      error: '',
      name: attr,
      touched: true,
    };

    return {
      ...state,
      editImportForm,
    };
  }

  if (editForm) {
    editForm[attr] = silent
      ? value
      : {
          value,
          error: validate(attr, value, state),
          name: attr,
          touched: true,
        };

    return { ...state, editForm: { ...editForm } };
  }

  return {
    ...state,
  };
}

function onChangeNestedProperty(state, { payload }) {
  const { attrs, value, touched = true } = payload;
  const editForm = { ...state.editForm };
  const { skills } = state;

  if (attrs.length > 2) {
    let data = null;
    const name = attrs[attrs.length - 1];
    for (let i = 0; i < attrs.length - 1; i += 1) {
      data = (data || editForm)[attrs[i]];
    }

    data[name] = {
      name: attrs.join('.'),
      // `touched: false` means "do not newly touch this field", never "un-touch it" - an error
      // already on screen has to stay there. Only the `attrs.length > 2` shape honours the flag.
      touched: touched || Boolean(data[name] && data[name].touched),
      error: validate(attrs.join('.'), value, state),
      value,
    };
  } else if (attrs.length === 2) {
    if (editForm[attrs[0]] === undefined) {
      editForm[attrs[0]] = {};
    }
    editForm[attrs[0]][attrs[1]] = getValue(attrs, value, state);
  } else {
    editForm[attrs[0]] = getValue(attrs, value, state);
  }

  if (['currentPositions', 'futurePositions'].includes(attrs[0]) && attrs[2] === 'id') {
    skills.map(skill => {
      if (skill.id === value) {
        const { bill, pay } = editForm[attrs[0] === 'currentPositions' ? 'current_rate' : 'future_rate'];
        skill.levels.map(tier => {
          editForm[attrs[0]][attrs[1]][tier.id] = {
            bill: {
              name: [attrs[0], attrs[1], tier.id, 'bill'].join('.'),
              touched: false,
              error: '',
              value: bill.value,
            },
            pay: {
              name: [attrs[0], attrs[1], tier.id, 'pay'].join('.'),
              touched: false,
              error: '',
              value: pay.value,
            },
          };

          return tier;
        });
      }

      return skill;
    });
  }

  return { ...state, editForm };
}

function onChangeStep(state, { payload }) {
  return {
    ...state,
    step: payload.step,
  };
}

function onCancel(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onTouch(state, { payload }) {
  const editForm = JSON.stringify(payload.editForm);

  return {
    ...state,
    editForm: JSON.parse(editForm.replace(/"touched":false/g, '"touched":true')),
  };
}

function onUpdatePositionsRequest(state) {
  return {
    ...state,
    loading: true,
  };
}

function onUpdatePositionsSuccess(state) {
  return {
    ...state,
    isEdit: false,
    loading: true,
    editForm: null,
  };
}

export default createReducer(initState, {
  [actions.ADD_FAIL]: onAddFail,
  [actions.ADD_SUCCESS]: onAddSuccess,
  [actions.CANCEL]: onCancel,
  [actions.CHANGE_NESTED_PROPERTY]: onChangeNestedProperty,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.REVALIDATE_BANDING_MAPPINGS]: onRevalidateBandingMappings,
  [actions.CHANGE_STEP]: onChangeStep,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.CREATE]: onCreate,
  [actions.EDIT]: onEdit,
  [actions.LOAD]: onLoad,
  [actions.TOGGLE_IMPORT_MODAL]: onToggleImportModal,
  [actions.UPDATE_POSITIONS_REQUEST]: onUpdatePositionsRequest,
  [actions.UPDATE_POSITIONS_SUCCESS]: onUpdatePositionsSuccess,
  [actions.LOAD_CURRENT_POSITIONS]: onLoadCurrentPositions,
  [actions.LOAD_CURRENT_POSITIONS_SUCCESS]: onLoadCurrentPositionsSuccess,
  [actions.LOAD_FORM_DATA]: onLoadFormData,
  [actions.LOAD_FORM_DATA_SUCCESS]: onLoadFormDataSuccess,
  [actions.LOAD_FUTURE_POSITIONS]: onLoadFuturePositions,
  [actions.LOAD_FUTURE_POSITIONS_SUCCESS]: onLoadFuturePositionsSuccess,
  [actions.LOAD_SECTOR_POSITIONS_SUCCESS]: onLoadSectorPositionsSuccess,
  [actions.LOAD_TIME_BANDS_SUCCESS]: onLoadTimeBandsSuccess,
  [actions.IMPORT_CSV_POSITIONS]: onImportCsvPositions,
  [actions.IMPORT_CSV_POSITIONS_SUCCESS]: onImportCsvPositionsSuccess,
  [actions.IMPORT_CSV_POSITIONS_ERROR]: onImportCsvPositionsError,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.SAVE_FAIL]: onSaveFail,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.TOUCH]: onTouch,
});
